import React, { useState, useMemo } from 'react';
import { clampPercentage } from '../../../../utils/formProgress';
import { SearchIcon, DocumentTextIcon, CheckIcon } from '@heroicons/react/solid';
import FormDetailsPanel from './FormDetailsPanel';
import { listModularInformantHistoryForms } from '../utils/buildModularPatientOverviewModel';
import InformantFeedbackButton from './InformantFeedbackButton';

/**
 * InformantHistoryPanel
 *
 * Mirrors PatientHistoryPanel for Adult Autism informants. Informants only
 * complete history questionnaires (no rating scales), so this panel lists those
 * seven forms and opens FormDetailsPanel with source: 'informant'.
 *
 * The parent passes a single informant's enriched view object (profile +
 * formData built from formInstances) plus the informantId for FormDetailsPanel.
 */
export default function InformantHistoryPanel({
  informantId,
  informantViewData,
  patientData,
  displayMode = 'full',
  panelTitle = 'Informant History',
  /** When multiple informants are assigned, tabs render inside this panel above the form list. */
  informantTabs = null,
  activeInformantTabId = null,
  onInformantTabChange,
  /** For single-informant display: show their name and relationship in the panel header. */
  informantDisplayName = null,
  informantRelationship = null,
  // When true, skip the outer card and the panel title. The CYP overview
  // wraps this list inside a parent/carer or school card that already has
  // its own heading, so a second card would look like a box inside a box.
  embedded = false,
  /** What to call this person in copy — CYP passes 'parent/carer' or
   *  'school representative' so the UI never says a generic "informant". */
  contactNoun = 'informant',
}) {
  const [selectedForm, setSelectedForm] = useState(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  const formTitles = useMemo(() => {
    if (!informantViewData?.formInstances) return [];
    return listModularInformantHistoryForms(informantViewData.formInstances);
  }, [informantViewData?.formInstances]);

  const formData = informantViewData?.formData || {};

  const handleFormClick = (formKey) => {
    setSelectedForm(formKey);
    setIsPanelOpen(true);
  };

  const handleClosePanel = () => {
    setIsPanelOpen(false);
  };

  const renderProgressIndicator = (formKey) => {
    const entry = formData[formKey];

    if (!entry || !entry.progress) {
      return (
        <div className="flex items-center justify-center h-6 w-6 rounded-full bg-gray-100">
          <DocumentTextIcon className="h-3.5 w-3.5 text-gray-400" />
        </div>
      );
    }

    const { progressPercentage, answeredQuestions, totalQuestions } = entry.progress;
    const clampedPercentage = clampPercentage(Math.round(progressPercentage || 0));

    if (progressPercentage === 0 || answeredQuestions === 0) {
      return (
        <div className="flex items-center justify-center h-6 w-6 rounded-full bg-gray-100">
          <DocumentTextIcon className="h-3.5 w-3.5 text-gray-400" />
        </div>
      );
    }

    if (clampedPercentage >= 100 || (answeredQuestions !== undefined && totalQuestions !== undefined && answeredQuestions >= totalQuestions)) {
      return (
        <div className="flex items-center justify-center h-6 w-6 rounded-full bg-green-100">
          <CheckIcon className="h-3.5 w-3.5 text-green-600" />
        </div>
      );
    }

    return (
      <div className="flex items-center justify-center h-6 w-6 rounded-full bg-violet-100">
        <DocumentTextIcon className="h-3.5 w-3.5 text-violet-600" />
      </div>
    );
  };

  const informantDataForPanel = informantId && informantViewData
    ? { [informantId]: informantViewData }
    : {};

  const headerRow = (
    <div className={`flex justify-between items-center ${embedded ? 'mb-2' : 'mb-4'}`}>
      {embedded ? (
        <p className="text-left text-sm text-gray-600">
          {`Click on any form to view the ${contactNoun}'s responses.`}
        </p>
      ) : (
        <div className="flex items-baseline gap-2">
          <h2 className="text-xl font-semibold text-gray-900">{panelTitle}</h2>
          {/* Show informant identity in the header when only one informant (no tabs) */}
          {!informantTabs && informantDisplayName && (
            <span className="text-sm text-gray-500">
              {informantDisplayName}
              {informantRelationship ? ` (${informantRelationship})` : ''}
            </span>
          )}
        </div>
      )}
      {/* In embedded mode the parent card owns the feedback button (card
          header, top-right), so we skip it here to avoid a duplicate icon. */}
      {!embedded && (
        <InformantFeedbackButton
          informantViewData={informantViewData}
          informantId={informantId}
          contactNoun={contactNoun}
        />
      )}
    </div>
  );

  return (
    <div className={embedded ? 'w-full' : 'w-full p-5 bg-white rounded-xl shadow border border-gray-200'}>
      {headerRow}

      {!embedded && (
        <p className="text-left text-sm text-gray-600 mb-4">
          {`Click on any form to view the ${contactNoun}'s responses.`}
        </p>
      )}

      {informantTabs && informantTabs.length > 1 && (
        <div className="mb-4">
          <div className="flex border-b border-gray-200 overflow-x-auto">
            {informantTabs.map((tab) => {
              const isActive = tab.id === activeInformantTabId;
              return (
                <button
                  key={tab.id}
                  type="button"
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    isActive
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                  onClick={() => onInformantTabChange?.(tab.id)}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="border rounded-lg overflow-hidden">
        {formTitles.length === 0 ? (
          <div className="p-4 text-sm text-gray-500 text-left">
            {`No ${contactNoun} history questionnaires have been assigned yet.`}
          </div>
        ) : formTitles.map((form, index) => {
          const entry = formData[form.key];
          if (!entry) return null;

          return (
            <div
              key={form.key}
              className={`p-3 cursor-pointer hover:bg-gray-50 transition-colors
                ${index !== formTitles.length - 1 ? 'border-b' : ''}`}
              onClick={() => handleFormClick(form.key)}
            >
              <div className="flex flex-col gap-2 md:hidden">
                <div className="flex items-center justify-between">
                  <div className="flex items-center flex-1 min-w-0">
                    {renderProgressIndicator(form.key)}
                    <span className="font-medium ml-3 truncate">{form.title}</span>
                  </div>
                  <div
                    className="flex items-center justify-center w-8 h-8 text-violet-800 rounded-full hover:bg-violet-100 transition-colors flex-shrink-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleFormClick(form.key);
                    }}
                  >
                    <SearchIcon className="h-5 w-5" />
                  </div>
                </div>

                {entry?.progress?.progressPercentage > 0 && (
                  <div className="ml-9">
                    <span className="text-xs font-medium text-gray-500">
                      {clampPercentage(Math.round(entry.progress.progressPercentage))}% complete
                    </span>
                  </div>
                )}
              </div>

              <div className="hidden md:flex md:justify-between md:items-center">
                <div className="flex items-center">
                  {renderProgressIndicator(form.key)}
                  <span className="font-medium ml-3">{form.title}</span>

                  {entry?.progress?.progressPercentage > 0 && (
                    <span className="ml-2 text-xs font-medium text-gray-500">
                      ({clampPercentage(Math.round(entry.progress.progressPercentage))}% complete)
                    </span>
                  )}
                </div>

                <div
                  className="flex items-center gap-1 text-xs text-violet-800 font-medium rounded-full px-1 py-1 transition-all duration-200 hover:bg-violet-100 cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFormClick(form.key);
                  }}
                >
                  <SearchIcon className="h-3 w-3" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <FormDetailsPanel
        isOpen={isPanelOpen && !!selectedForm}
        formInfo={{
          key: selectedForm,
          source: 'informant',
          informantId,
        }}
        patientData={patientData}
        informantData={informantDataForPanel}
        onClose={handleClosePanel}
        displayMode={displayMode}
      />

    </div>
  );
}
